import React, { useRef, useState } from "react"
import { useQueryClient } from "react-query"
import { POSTS_QUERY_KEY } from "../../hooks/usePosts"
import { Button, OutlineButton } from "../shared/Button"
import { Spinner } from "../shared/Spinner"
import {
  createPlaylist,
  updatePlaylist,
  deletePlaylist,
  uploadPlaylistImage,
} from "../../util/backend_api/playlists"
import { useHistory } from "react-router-dom"
import { root_path } from "../../routes"
import { PlaylistPost, PlaylistSong } from "../../types/playlists"
import { PLAYLIST_PLACEHOLDER_IMAGE_URL } from "../../util/constants"
import { GripVertical, Trash } from "react-bootstrap-icons"
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd"
import clsx from "clsx"

interface CreatePlaylistFormProps {
  bandId: number
  existingPlaylist?: PlaylistPost
  songs?: PlaylistSong[]
  onRemoveSong?: (song: PlaylistSong) => void
  onDragEnd?: (result: DropResult) => void
  onSuccess?: (coverImageUrl?: string) => void
  onDelete?: () => void
  onCancel?: () => void
}

const CreatePlaylistForm: React.FC<CreatePlaylistFormProps> = ({
  bandId,
  existingPlaylist,
  songs,
  onRemoveSong,
  onDragEnd,
  onSuccess,
  onDelete,
  onCancel,
}) => {
  const queryClient = useQueryClient()
  const isEditing = !!existingPlaylist
  const playlistModelId = existingPlaylist?.playlist_id ?? existingPlaylist?.content_hash?.playlist_id

  const history = useHistory()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState(existingPlaylist?.title ?? "")
  const [description, setDescription] = useState(
    existingPlaylist?.content_hash?.description ?? existingPlaylist?.description ?? "",
  )
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(
    existingPlaylist?.external_photo_src ?? null,
  )
  const [isPublic, setIsPublic] = useState(
    existingPlaylist?.is_public ?? existingPlaylist?.content_hash?.is_public ?? true,
  )
  const [removeCustomArtwork, setRemoveCustomArtwork] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImageFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setImagePreviewUrl(ev.target?.result as string)
    reader.readAsDataURL(file)
    e.target.value = ""
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      window.location.href = root_path()
    }
  }

  const handleDelete = async () => {
    if (!playlistModelId) return
    if (!window.confirm("Are you sure you want to delete this playlist? This cannot be undone.")) return
    setIsSubmitting(true)
    const response = await deletePlaylist(playlistModelId)
    setIsSubmitting(false)
    if (response?.success) {
      queryClient.invalidateQueries(POSTS_QUERY_KEY)
      if (onDelete) onDelete()
    } else {
      setFormError(response?.error || "Failed to delete playlist.")
    }
  }

  const onSubmit = async () => {
    if (!name.trim()) {
      setFormError("Title is required.")
      return
    }
    setIsSubmitting(true)
    setFormError(null)

    let coverImageSignedId: string | undefined
    if (imageFile) {
      try {
        coverImageSignedId = await uploadPlaylistImage(imageFile)
      } catch {
        setFormError("Image upload failed. Please try again.")
        setIsSubmitting(false)
        return
      }
    }

    const params = {
      name: name.trim(),
      description: description.trim(),
      coverImageSignedId,
      removeCustomArtwork: removeCustomArtwork && !imageFile,
      isPublic,
    }

    const response = isEditing && playlistModelId
      ? await updatePlaylist(playlistModelId, params)
      : await createPlaylist({ bandId, ...params })

    setIsSubmitting(false)

    if (response?.success) {
      if (onSuccess) {
        const isRemoving = removeCustomArtwork && !imageFile
        const coverUrl = isRemoving
          ? PLAYLIST_PLACEHOLDER_IMAGE_URL
          : (response?.playlist?.post?.cover_image_url || undefined)
        onSuccess(coverUrl)
      } else if (response?.playlist?.nada_path) {
        history.push(response.playlist.nada_path)
      }
    } else {
      setFormError(response?.error || "Something went wrong. Please try again.")
    }
  }

  return (
    <div className="grid grid-cols-12 mb-6">
      {/* Left column: cover image upload */}
      <div className="lg:col-span-7 col-span-12">
        <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold mb-1">
          Cover Image
        </label>
        <div
          className="w-full border-2 border-gray-300 bg-gray-200 overflow-hidden"
          style={{ aspectRatio: "1 / 1" }}
        >
          <img
            src={imagePreviewUrl || PLAYLIST_PLACEHOLDER_IMAGE_URL}
            alt="Cover"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex gap-2 mt-2">
          {(imageFile || existingPlaylist?.has_custom_artwork) && imagePreviewUrl && (
            <OutlineButton
              className="flex-1 text-gray-700"
              onClick={() => {
                setImageFile(null)
                setImagePreviewUrl(null)
                setRemoveCustomArtwork(true)
              }}
            >
              Remove Custom Image
            </OutlineButton>
          )}
          <OutlineButton
            className="flex-1 text-gray-700"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload New Image
          </OutlineButton>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            handleImageChange(e)
            setRemoveCustomArtwork(false)
          }}
        />
      </div>

      {/* Right column: fields and buttons */}
      <div className="lg:col-span-5 col-span-12 lg:pl-4 pt-2 lg:pt-0">
        <form onSubmit={(e) => { e.preventDefault(); onSubmit() }}>
          <div className="mb-4">
            <label className="block text-gray-900 text-xs uppercase font-bold mb-1">
              Title *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Playlist title"
              className="appearance-none border rounded w-full text-base py-2 px-3 leading-tight focus:outline-none text-gray-900"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-900 text-xs uppercase font-bold mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={4}
              className="appearance-none border rounded w-full text-base py-2 px-3 leading-tight focus:outline-none text-gray-900 resize-none"
            />
          </div>

          <div className="mb-4 flex items-center gap-2">
            <input
              type="checkbox"
              id="playlist-is-public"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="cursor-pointer"
            />
            <label htmlFor="playlist-is-public" className="text-gray-900 text-xs uppercase font-bold cursor-pointer">
              Public
            </label>
          </div>

          {formError && (
            <div className="col-span-12 text-red-700 lg:text-sm text-lg mb-3">
              {formError}
            </div>
          )}

          <div className="mt-4">
            {isSubmitting ? (
              <Spinner />
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <Button
                    disabled={!name.trim()}
                    bg="bg-green-700"
                    className="mr-2"
                    onClick={onSubmit}
                  >
                    {isEditing ? "Update Playlist" : "Create Playlist"}
                  </Button>
                  <Button bg="bg-gray-400" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
                {isEditing && playlistModelId && (
                  <Button bg="bg-red-600" onClick={handleDelete}>
                    Delete
                  </Button>
                )}
              </div>
            )}
          </div>

          {songs && songs.length > 0 && onDragEnd && onRemoveSong && (
            <div className="mt-6">
              <label className="block text-gray-900 text-xs uppercase font-bold mb-2">
                Tracks
              </label>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="playlist-songs-edit">
                  {(provided) => (
                    <ul
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="text-sm text-black"
                    >
                      {songs.map((song, index) => (
                        <Draggable
                          key={song.playlist_post_id ?? song.id}
                          draggableId={String(song.playlist_post_id ?? song.id)}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <li
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={clsx(
                                "flex items-center justify-between py-1 border-b border-gray-100",
                                snapshot.isDragging && "opacity-75 bg-white shadow",
                              )}
                            >
                              <div className="flex items-center min-w-0 gap-1">
                                <span
                                  {...provided.dragHandleProps}
                                  className="text-gray-400 cursor-grab flex-none"
                                >
                                  <GripVertical size={14} />
                                </span>
                                <span className="text-gray-400 flex-none text-xs w-5 text-right">
                                  {index + 1}.
                                </span>
                                <span className="truncate text-gray-800 ml-1">{song.title}</span>
                                <span className="text-gray-400 text-xs flex-none ml-1 truncate">
                                  {song.band_name}
                                </span>
                              </div>
                              <button
                                type="button"
                                className="text-gray-400 hover:text-red-500 focus:outline-none flex-none ml-2"
                                onClick={() => onRemoveSong(song)}
                              >
                                <Trash size={13} />
                              </button>
                            </li>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default CreatePlaylistForm
